// SidebarNav — primitivos compartidos para sidebars (Coordinador, Supervisor,
// Admin, SuperAdmin). Patrón del mockup sa-shared.jsx + crud-shared.jsx:
//   - Width 240px, bg surface-elevated, border-r border
//   - Items con padding 7px 12px, left accent border (2px) cuando active
//   - Bg accent-soft cuando active
//   - Items agrupados con label uppercase tracking-wider
//
//   <Sidebar>
//     <SidebarGroup label="Operación">
//       <SidebarItem to="/coordinador" icon="home" active>Dashboard</SidebarItem>
//       <SidebarItem to="/coordinador/programacion-lideres" icon="users">
//         Programar líderes
//       </SidebarItem>
//     </SidebarGroup>
//   </Sidebar>

import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon, type IconName } from '../ui/icon';
import { cn } from '../../lib/utils';

interface SidebarProps {
  children: ReactNode;
  className?: string;
  /** Si true, ancho colapsado a 56px (solo iconos). */
  collapsed?: boolean;
}

export function Sidebar({ children, className, collapsed = false }: SidebarProps) {
  return (
    <aside
      className={cn(
        'flex flex-shrink-0 flex-col overflow-y-auto border-r border-border bg-surface-elevated',
        collapsed ? 'w-14 px-2 py-5' : 'w-60 px-4 py-5',
        className,
      )}
    >
      {children}
    </aside>
  );
}

interface SidebarGroupProps {
  label?: string;
  children: ReactNode;
  className?: string;
  /**
   * Grupo PLEGABLE (política 4 de coherencia: menú corto, nada escondido).
   * El grupo que contiene la ruta activa se abre solo; el resto recuerda la
   * última elección del usuario en localStorage.
   */
  collapsible?: boolean;
  /** Estado inicial cuando no hay preferencia guardada ni ruta activa dentro. */
  defaultOpen?: boolean;
  /**
   * Rutas que "pertenecen" al grupo — si la ubicación actual matchea, el grupo
   * se abre solo. String = prefijo (matchea `r` y `r/…`); RegExp = test exacto
   * (para rutas index como `/coordinador`, que como prefijo matchearían todo).
   */
  rutas?: Array<string | RegExp>;
  /** Cantidad de entradas, mostrada cuando está plegado ("Configuración · 7"). */
  count?: number;
  /** Clave de persistencia (default: derivada del label). */
  storageKey?: string;
}

function leerPreferencia(key: string): boolean | null {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? null : v === '1';
  } catch {
    return null;
  }
}

function guardarPreferencia(key: string, abierto: boolean): void {
  try {
    window.localStorage.setItem(key, abierto ? '1' : '0');
  } catch {
    /* almacenamiento bloqueado: la preferencia vive solo en la sesión */
  }
}

export function SidebarGroup({
  label,
  children,
  className,
  collapsible = false,
  defaultOpen = true,
  rutas = [],
  count,
  storageKey,
}: SidebarGroupProps) {
  const { pathname } = useLocation();
  const key = storageKey ?? `sidebar:grupo:${label ?? 'sin-label'}`;
  const contieneActivo = rutas.some((r) =>
    r instanceof RegExp ? r.test(pathname) : pathname === r || pathname.startsWith(`${r}/`),
  );
  // Estado explícito (F1.2): al ENTRAR a una página del grupo se abre; después
  // el usuario puede cerrarlo y su elección se conserva (localStorage) — antes
  // el grupo activo quedaba forzado abierto y el toggle no respondía.
  const [abierto, setAbierto] = useState<boolean>(() => {
    if (!collapsible) return true;
    if (contieneActivo) return true;
    return leerPreferencia(key) ?? defaultOpen;
  });
  useEffect(() => {
    // Solo dispara en la TRANSICIÓN a "ruta dentro del grupo": navegar entre
    // dos páginas del mismo grupo no reabre lo que el usuario cerró.
    if (collapsible && contieneActivo) setAbierto(true);
  }, [collapsible, contieneActivo]);

  if (!collapsible) {
    return (
      <div className={cn('mb-4', className)}>
        {label ? (
          <p className="mb-1.5 px-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            {label}
          </p>
        ) : null}
        <div className="flex flex-col gap-0.5">{children}</div>
      </div>
    );
  }

  const toggle = () => {
    const siguiente = !abierto;
    setAbierto(siguiente);
    guardarPreferencia(key, siguiente);
  };

  return (
    <div className={cn('mb-2', className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={abierto}
        className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary transition-colors hover:bg-surface-overlay hover:text-fg-secondary"
      >
        <span className="flex-1 text-left">{label}</span>
        {!abierto && count ? <span className="tabular-nums normal-case">{count}</span> : null}
        <Icon
          name="chevron-right"
          size={12}
          stroke={2}
          className={cn('transition-transform', abierto && 'rotate-90')}
        />
      </button>
      {abierto ? <div className="mt-0.5 flex flex-col gap-0.5">{children}</div> : null}
    </div>
  );
}

interface SidebarItemProps {
  /** Si se pasa, el item es un NavLink. Si no, es un button con onClick. */
  to?: string;
  /** Si se pasa, exact match en NavLink (para "/coordinador" que matchea solo el index). */
  end?: boolean;
  icon?: IconName;
  badge?: ReactNode;
  badgeTone?: 'crit' | 'warn' | 'info';
  children: ReactNode;
  onClick?: () => void;
  /** Force active state (raro — usar NavLink en su lugar). */
  active?: boolean;
}

export function SidebarItem({
  to,
  end,
  icon,
  badge,
  badgeTone,
  children,
  onClick,
  active: forceActive,
}: SidebarItemProps) {
  const classes = (active: boolean) =>
    cn(
      'group relative flex items-center gap-2.5 rounded px-3 py-2 text-sm transition-colors',
      // Left accent border indicator
      'border-l-2',
      active
        ? 'border-l-accent bg-accent-soft font-medium text-fg'
        : 'border-l-transparent text-fg-secondary hover:bg-surface-overlay hover:text-fg',
    );

  const inner = (active: boolean) => (
    <>
      {icon ? (
        <Icon
          name={icon}
          size={14}
          stroke={1.5}
          className={active ? 'text-fg' : 'text-fg-tertiary'}
        />
      ) : null}
      <span className="flex-1 truncate">{children}</span>
      {badge ? (
        <span
          className={cn(
            'text-[10px] font-medium tabular-nums',
            badgeTone === 'crit' ? 'text-crit' : badgeTone === 'warn' ? 'text-warn' : 'text-info',
          )}
        >
          {badge}
        </span>
      ) : null}
    </>
  );

  if (to) {
    return (
      <NavLink to={to} end={end} className={({ isActive }) => classes(forceActive ?? isActive)}>
        {({ isActive }) => inner(forceActive ?? isActive)}
      </NavLink>
    );
  }
  return (
    <button type="button" onClick={onClick} className={classes(forceActive ?? false)}>
      {inner(forceActive ?? false)}
    </button>
  );
}
